import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Empty, Form, Input, message, Modal, Select,
  Space, Table, Tag, Typography
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined
} from '@ant-design/icons';
import { investmentsApi } from './investmentsApi';
import {
  INVESTMENT_CLASSES, friendlyErrorFor
} from './investmentsHelpers';
import '../accounts.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Investment Categories panel (Stage 6B.9E).
//
// Simple master list — code, name, investment_class, active flag.
// Lives inside the InvestmentsPage as a secondary tab.
// ─────────────────────────────────────────────────────────────────

function CategoryFormModal({ open, mode, record, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (mode === 'edit' && record) {
      form.setFieldsValue({
        categoryCode:    record.categoryCode,
        categoryName:    record.categoryName,
        investmentClass: record.investmentClass || 'other',
        isActive:        record.isActive !== false,
        remarks:         record.remarks
      });
    } else {
      form.setFieldsValue({ investmentClass: 'other', isActive: true });
    }
  }, [open, mode, record, form]);

  const save = useCallback(async () => {
    try {
      const v = await form.validateFields();
      setBusy(true);
      let saved;
      if (mode === 'add') {
        saved = await investmentsApi.createCategory(v);
        message.success(`Category ${saved.categoryCode} created.`);
      } else {
        saved = await investmentsApi.updateCategory(record.id, v);
        message.success('Category updated.');
      }
      onSaved && onSaved(saved);
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setBusy(false); }
  }, [form, mode, record, onSaved]);

  return (
    <Modal
      title={mode === 'add' ? 'New Investment Category' : `Edit Category — ${record?.categoryCode}`}
      open={open}
      onCancel={onClose}
      onOk={save}
      okText={mode === 'add' ? 'Create' : 'Save'}
      confirmLoading={busy}
      centered
      destroyOnClose
      width={620}
      maskClosable={false}
      className="erp-accounts-modal"
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form">
        <Form.Item label="Category Code" name="categoryCode"
          rules={[
            { required: true, message: 'Category code is required.' },
            { pattern: /^[A-Z0-9_-]+$/, message: 'Uppercase letters, digits, hyphens and underscores only.' }
          ]}>
          <Input maxLength={40} placeholder="e.g. BANK_FD" />
        </Form.Item>
        <Form.Item label="Category Name" name="categoryName"
          rules={[{ required: true, message: 'Category name is required.' }]}>
          <Input maxLength={120} placeholder="e.g. Bank Fixed Deposits" />
        </Form.Item>
        <Form.Item label="Investment Class" name="investmentClass"
          rules={[{ required: true, message: 'Investment class is required.' }]}>
          <Select options={INVESTMENT_CLASSES.map((c) => ({ value: c.value, label: c.label }))} />
        </Form.Item>
        <Form.Item label="Active" name="isActive" valuePropName="checked"
          tooltip="Inactive categories don't appear in the New Investment dropdown.">
          <Select style={{ width: 160 }} options={[
            { value: true, label: 'Active' }, { value: false, label: 'Inactive' }
          ]} />
        </Form.Item>
        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function InvestmentCategoriesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [modalRecord, setModalRecord] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const out = await investmentsApi.listCategories({ page: 1, pageSize: 200 });
      setRows((out && out.rows) || []);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openAdd  = useCallback(() => { setModalMode('add'); setModalRecord(null); setModalOpen(true); }, []);
  const openEdit = useCallback((r) => { setModalMode('edit'); setModalRecord(r); setModalOpen(true); }, []);
  const onSaved  = useCallback(async () => { setModalOpen(false); await fetchCategories(); }, [fetchCategories]);

  const handleDelete = useCallback(async (record) => {
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Delete category ${record.categoryCode}?`,
        content: 'The category will be soft-deleted. Categories linked to active investments cannot be deleted.',
        okText: 'Delete', okButtonProps: { danger: true }, cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await investmentsApi.deleteCategory(record.id);
      message.success('Category deleted.');
      await fetchCategories();
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, [fetchCategories]);

  const columns = useMemo(() => [
    { title: 'Code', dataIndex: 'categoryCode', key: 'categoryCode', width: 160,
      render: (v) => <code className="erp-grid__code">{v}</code> },
    { title: 'Name', dataIndex: 'categoryName', key: 'categoryName' },
    { title: 'Class', dataIndex: 'investmentClass', key: 'investmentClass',
      width: 130,
      render: (v) => v ? <Tag>{v}</Tag> : <span className="erp-grid__muted">—</span> },
    { title: 'Active', dataIndex: 'isActive', key: 'isActive', width: 90,
      render: (v) => v ? <Tag color="green">Active</Tag> : <Tag color="default">Inactive</Tag> },
    { title: '', key: 'actions', width: 100, align: 'right',
      render: (_v, row) => (
        <Space size={4}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} />
        </Space>
      ) }
  ], [openEdit, handleDelete]);

  return (
    <div className="erp-accounts-page">
      <Space style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
        <Text strong>Investment Categories</Text>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchCategories} loading={loading}>
            Refresh
          </Button>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            New Category
          </Button>
        </Space>
      </Space>
      {loadError ? (
        <Alert type="error" showIcon style={{ marginBottom: 10 }}
          message="Could not load investment categories"
          description={friendlyErrorFor(loadError)}
          action={<Button size="small" icon={<ReloadOutlined />} onClick={fetchCategories}>Retry</Button>} />
      ) : null}
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 25, size: 'small' }}
        locale={{ emptyText: <Empty description="No investment categories yet." /> }}
      />
      <CategoryFormModal
        open={modalOpen}
        mode={modalMode}
        record={modalRecord}
        onClose={() => setModalOpen(false)}
        onSaved={onSaved}
      />
    </div>
  );
}
